import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAPI } from '../../api';
import PageSection from '../../components/PageSection';
import PropEditor from '../../components/PropEditor';
import DataTable from '../../components/DataTable';
import RawJsonDisplay from '../../components/RawJsonDisplay';
import { AccountInfo, AccountMemberInfo, TenantInfo, InviteForm } from 'types';
import { OrgContextLink } from 'components/OrgContextProvider';

const AccountContainer = () => {
	const api = useAPI();
	const { accountID } = useParams();

	const [account, setAccount] = useState<AccountInfo | null>(null);
	const [members, setMembers] = useState<AccountMemberInfo[]>([]);
	const [tenants, setTenants] = useState<TenantInfo[]>([]);
	const [inviteForm, setInviteForm] = useState<InviteForm>({ username: '', display: '', role: 'member' });

	const loadAccount = () => {
		api.get<AccountInfo>(`/api/accounts/${accountID}`)
		.then(json => setAccount(json))
		.catch(() => setAccount(null));
	};

	const loadMembers = () => {
		api.get<AccountMemberInfo[]>(`/api/accounts/${accountID}/members`)
		.then(json => setMembers(json));
	};

	const loadTenants = () => {
		api.get<TenantInfo[]>(`/api/accounts/${accountID}/tenants`)
		.then(json => setTenants(json));
	};

	useEffect(() => {
		loadAccount();
		loadMembers();
		loadTenants();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountID]);

	const saveAccount = () => {
		api.put(`/api/accounts/${accountID}`, { name: account?.name })
		.then(loadAccount);
	};

	const removeMember = (userID: string) => {
		if (window.confirm('Remove this member from the account?')) {
			api.delete(`/api/accounts/${accountID}/members/${userID}`)
			.then(loadMembers);
		}
	};

	const updateMemberRole = (userID: string, role: 'admin' | 'member') => {
		api.put(`/api/accounts/${accountID}/members/${userID}`, { role })
		.then(loadMembers);
	};

	const sendInvite = () => {
		if (!inviteForm.username.trim()) return alert('Username is required');

		api.post(`/api/accounts/${accountID}/invite`, inviteForm)
		.then(() => {
			alert('Invite sent successfully');
			setInviteForm({ username: '', display: '', role: 'member' });
		});
	};

	return (
		<div>
			<PageSection title={`Account: ${account?.name ?? accountID}`} level={2} />

			<PageSection>
				<OrgContextLink to="/settings/accounts">&#x25c0; Back to Accounts</OrgContextLink>
			</PageSection>

			<PageSection title="Account Info">
				<PropEditor<AccountInfo | null>
					object={account}
					onChange={(obj) => setAccount(obj)}
					props={[
						{ label: 'Name', key: 'name' },
						{ label: 'ID', key: 'id', disabled: true }
					]}
					tools={[
						() => (<button type="button" onClick={saveAccount}>Save Account</button>)
					]}
				/>
			</PageSection>

			<PageSection title="Tenants" level={3}>
				<DataTable<TenantInfo>
					data={tenants}
					columns={[
						{ label: 'Name', key: 'name', render: (tenant) => <OrgContextLink to={`/settings/tenants/${tenant.id}`}>{tenant.name}</OrgContextLink> },
						{ label: 'ID', key: 'id' }
					]}
				/>
				<p><OrgContextLink to={`/settings/tenants/new?accountID=${accountID}`}>Create new tenant in this account...</OrgContextLink></p>
			</PageSection>

			<PageSection title="Members" level={3}>
				<DataTable<AccountMemberInfo>
					data={members}
					columns={[
						{ label: 'User ID', key: 'userID' },
						{ label: 'Role', key: 'role' }
					]}
					tools={[
						{
							label: 'Make Admin',
							render: (member) => member.role !== 'admin'
								? <button onClick={() => updateMemberRole(member.userID, 'admin')}>Make Admin</button>
								: null
						},
						{
							label: 'Make Member',
							render: (member) => member.role !== 'member'
								? <button onClick={() => updateMemberRole(member.userID, 'member')}>Make Member</button>
								: null
						},
						{ label: 'Remove', action: (member) => removeMember(member.userID) }
					]}
				/>
			</PageSection>

			<PageSection title="Invite User" level={3}>
				<table>
					<tbody>
						<tr>
							<td>Username (email)</td>
							<td><input type="text" value={inviteForm.username} onChange={e => setInviteForm({ ...inviteForm, username: e.target.value })} /></td>
						</tr>
						<tr>
							<td>Display Name (optional)</td>
							<td><input type="text" value={inviteForm.display} onChange={e => setInviteForm({ ...inviteForm, display: e.target.value })} /></td>
						</tr>
						<tr>
							<td>Role</td>
							<td>
								<select value={inviteForm.role} onChange={e => setInviteForm({ ...inviteForm, role: e.target.value as 'admin' | 'member' })}>
									<option value="member">Member</option>
									<option value="admin">Admin</option>
								</select>
							</td>
						</tr>
						<tr>
							<td></td>
							<td><input type="button" value="Send Invite" onClick={sendInvite} /></td>
						</tr>
					</tbody>
				</table>
			</PageSection>

			<PageSection title="Raw" level={3}>
				<RawJsonDisplay data={{ account, members, tenants }} />
			</PageSection>
		</div>
	);
};

export default AccountContainer;
